import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { notification } from 'antd';
import { camelizeKeys, decamelizeKeys } from 'humps';
import i18next from 'i18next';
import {
  isBackendDisabled,
  isBackendUrlDisabled,
} from './backend-availability';

const avoidErrorMessage: string[] = [
  'INVALID_SESSION_TOKEN',
  'REJECTED_EVENTS',
];

interface RespData<T> {
  data: T;
  status: 'success' | 'error';
  rowcount?: number;
  error?: string;

  [key: string]: any;
}

interface BackendAuthToken {
  name: string;
  url: string;
  token: string;
}

const backendTokensStorageKey =
  'admin-backend-auth-tokens';

function getBackendAuthTokens(): BackendAuthToken[] {
  try {
    const stored = localStorage.getItem(
      backendTokensStorageKey,
    );

    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);

    return Array.isArray(parsed)
      ? parsed
      : [];
  } catch {
    return [];
  }
}

function getBackend(
  backendName: string,
): BackendAuthToken | null {
  const backends =
    getBackendAuthTokens();

  return (
    backends.find(
      (item) =>
        item.name === backendName,
    ) || null
  );
}

function getBackendAuthToken(
  backendName: string,
): string | null {
  const backend =
    getBackend(backendName);

  return backend?.token || null;
}

function handleError(error: AxiosError): void {
  const response: AxiosResponse | undefined =
    error.response;

  if (axios.isCancel(error)) {
    return;
  }

  /*
   * Requests which explicitly disable error alerts
   * must not show notifications.
   */
  if (
    response?.config?.headers &&
    (response.config.headers as any)
      .errorAlert === false
  ) {
    return;
  }

  /*
   * Session 401 should not show notification.
   */
  if (
    response?.config.url?.endsWith(
      '/api/users/session',
    ) &&
    response.status === 401
  ) {
    return;
  }

  /*
   * Don't show error message if resource not found.
   */
  if (response?.status === 404) {
    return;
  }

  let errorDesc = 'Server Error';

  if (
    response?.data?.message &&
    typeof response?.data?.message ===
      'string'
  ) {
    errorDesc = i18next.t(
      response.data.message,
    );
  } else if (response?.statusText) {
    errorDesc = i18next.t(
      response.statusText,
    );
  }

  const additionalErrorMessage =
    response?.data?.err_extra
      ? response.data.err_extra.map(
          (item: any) => {
            if (typeof item === 'string') {
              return i18next.t(item);
            }

            if (Array.isArray(item)) {
              return item.join(', ');
            }

            return item.toString();
          },
        )
      : [];

  if (
    avoidErrorMessage.indexOf(
      response?.data?.message,
    ) === -1
  ) {
    notification.error({
      message: `${i18next.t(errorDesc)}`,
      description:
        additionalErrorMessage.join(' '),
      duration: 4,
    });
  }
}

const httpInstance = axios.create();

httpInstance.defaults.withCredentials =
  true;

httpInstance.defaults.headers.common.isLoading =
  true;

httpInstance.defaults.headers.common.errorAlert =
  true;

Object.setPrototypeOf(
  httpInstance,
  axios,
);

httpInstance.interceptors.request.use(
  function (
    config: InternalAxiosRequestConfig,
  ) {
    const newConfig = {
      ...config,
    };

    const backendName =
      (config as any).backend;

    if (
      isBackendDisabled(backendName) ||
      isBackendUrlDisabled(config.url)
    ) {
      return Promise.reject(
        new axios.CanceledError(
          `Backend ${backendName || config.url} is disabled`,
        ),
      );
    }

    /*
     * Login requests don't have a token yet.
     */
    const isLoginRequest =
      !!config.url?.endsWith(
        '/api/users/challenge/login',
      );

    /*
     * If a backend was specified, resolve
     * BOTH its URL and its token.
     *
     * Example:
     *
     * backend: 'S1'
     * url: '/api/nodes'
     *
     * becomes:
     *
     * https://s1-newadmin.premierbet.me/api/nodes
     *
     * with:
     *
     * Auth-Token: S1_TOKEN
     */
    if (backendName) {
      const backend =
        getBackend(backendName);

      if (!backend) {
        return Promise.reject(
          new Error(
            `Backend ${backendName} nije pronađen`,
          ),
        );
      }

      /*
       * Do not modify absolute URLs.
       *
       * This is important for login/session
       * requests which may already contain the
       * complete backend URL.
       */
      if (
        config.url &&
        !/^https?:\/\//i.test(config.url)
      ) {
        newConfig.url =
          `${backend.url.replace(
            /\/+$/,
            '',
          )}/${config.url.replace(
            /^\/+/,
            '',
          )}`;
      } else {
        newConfig.url = config.url;
      }

      /*
       * Add the token belonging to this
       * exact backend.
       */
      if (
        !isLoginRequest &&
        newConfig.headers
      ) {
        const authToken =
          getBackendAuthToken(
            backendName,
          );

        if (authToken) {
          (
            newConfig.headers as any
          )['Auth-Token'] = authToken;
        }
      }
    } else {
      /*
       * No backend specified.
       *
       * Keep the original URL untouched.
       */
      newConfig.url = config.url;
    }

    /*
     * Don't modify multipart/form-data requests.
     */
    if (
      newConfig.headers &&
      newConfig.headers['Content-Type'] ===
        'multipart/form-data'
    ) {
      return newConfig;
    }

    if (config.params) {
      newConfig.params =
        decamelizeKeys(
          config.params,
          function (
            key,
            convert,
            options,
          ) {
            return /^[A-Z0-9_]+$/.test(key)
              ? key
              : convert(key, options);
          },
        );
    }

    if (config.data) {
      newConfig.data =
        decamelizeKeys(
          config.data,
          function (
            key,
            convert,
            options,
          ) {
            return /^[A-Z0-9_]+$/.test(key)
              ? key
              : convert(key, options);
          },
        );
    }

    return newConfig;
  },
  function (error: AxiosError) {
    handleError(error);

    return Promise.reject(
      error.response,
    );
  },
);

httpInstance.interceptors.response.use(
  function (res: AxiosResponse) {
    const headers =
      res.config.headers;

    const data: RespData<any> =
      res.data;

    if (
      res.data &&
      res.data.status === 'error' &&
      headers?.errorAlert
    ) {
      notification.error({
        message: `Error Code: ${
          data.errorCode ?? -1
        }`,
        description: i18next.t(
          (data.msg ||
            data.message) ??
            'Server Error',
        ),
      });

      return Promise.reject(
        res.data,
      );
    }

    if (
      res.data &&
      res.headers['content-type'] ===
        'application/json'
    ) {
      res.data = camelizeKeys(
        res.data,
        function (
          key,
          convert,
        ) {
          return /^[A-Z0-9_]+$/.test(key)
            ? key
            : convert(key);
        },
      );

      if (headers?.successAlert) {
        notification.success({
          message:
            data.msg ??
            'Success Action',
          placement:
            'bottomLeft',
          duration: 1,
        });
      }
    }

    return res.data;
  },
  function (error: AxiosError) {
    handleError(error);

    if (
      error.response &&
      error.response.data
    ) {
      error.response.data =
        camelizeKeys(
          error.response.data,
          function (
            key,
            convert,
          ) {
            return /^[A-Z0-9_]+$/.test(key)
              ? key
              : convert(key);
          },
        );
    }

    return Promise.reject(
      error.response,
    );
  },
);

export default httpInstance;
